import { useCallback, useEffect, useRef, useState } from "react";

import type { CheckoutEventsData } from "@paddle/paddle-js";
import { CheckoutEventNames } from "@paddle/paddle-js";
import { SegmentedControl, SpinnerCenter } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { type PaddleBillingPrice, useInitialPeriod } from "../../hooks/useCheckout";
import { useEventListener } from "../../hooks/useEventListener";
import { usePaddleBilling } from "../../hooks/usePaddleBilling";
import { useVerifyPaddleBillingTransaction } from "../../hooks/usePaddleBillingCheckout";
import { useTracker } from "../../hooks/useTracker";
import { PaddleBillingAdapter } from "../../lib/PaddleBillingAdapter";
import { SubscriptionPeriod } from "../../types";
import Heading from "../Heading/Heading";

import style from "./PaddleBillingCheckout.module.css";

interface CheckoutSummary {
  items: Array<{ name: string; subtotal: number }>;
  discount: number;
  tax: number;
  total: number;
  currencyCode: string;
}

interface PaddleBillingCheckoutProps {
  prices: PaddleBillingPrice[];
  customerId?: string;
}

const formatCurrency = (amount: number, currencyCode: string) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

const buildSummary = (data: CheckoutEventsData): CheckoutSummary => ({
  items: data.items.map((item) => ({
    name: item.product.name,
    subtotal: item.totals.subtotal,
  })),
  discount: data.totals.discount,
  tax: data.totals.tax,
  total: data.totals.total,
  currencyCode: data.currency_code,
});

export const PaddleBillingCheckout = ({ prices, customerId }: PaddleBillingCheckoutProps) => {
  const { data: accountData } = useAccount();
  const { isLoaded: isPaddleBillingLoaded, error: paddleBillingError } = usePaddleBilling();
  const {
    mutate: mutateVerifyPaddleBillingTransaction,
    isPending: isVerifyPaddleBillingTransactionPending,
  } = useVerifyPaddleBillingTransaction();
  const tracker = useTracker();
  const initialPeriod = useInitialPeriod();
  const navigation = useNavigation();

  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>(initialPeriod);
  const [isCheckoutReady, setIsCheckoutReady] = useState(false);
  const [checkoutSummary, setCheckoutSummary] = useState<CheckoutSummary | null>(null);
  const isCheckoutSuccess = useRef(false);
  const checkoutOpened = useRef(false);

  const selectedPrice = prices.find((p) => p.period === selectedPeriod);
  const selectedPriceId = selectedPrice?.priceId;

  useEventListener("beforeunload", tracker.trackCheckoutAbandonment.bind(tracker));
  useEffect(() => {
    return () => {
      if (!isCheckoutSuccess.current) {
        tracker.trackCheckoutAbandonment();
      }
    };
  }, [tracker]);

  const handleCheckoutComplete = useCallback(
    (transactionId: string) => {
      mutateVerifyPaddleBillingTransaction(transactionId, {
        onSuccess: () => {
          isCheckoutSuccess.current = true;
          navigation.goToNonRecurringPaymentSuccess();
        },
      });
    },
    [mutateVerifyPaddleBillingTransaction, navigation]
  );

  useEffect(() => {
    const adapter = PaddleBillingAdapter.getInstance();
    adapter.setEventCallback((event) => {
      if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED && event.data?.transaction_id) {
        handleCheckoutComplete(event.data.transaction_id);
      }
      if (
        (event.name === CheckoutEventNames.CHECKOUT_LOADED ||
          event.name === CheckoutEventNames.CHECKOUT_UPDATED ||
          event.name === CheckoutEventNames.CHECKOUT_ITEMS_UPDATED) &&
        event.data
      ) {
        setCheckoutSummary(buildSummary(event.data));
        setIsCheckoutReady(true);
      }
    });
    return () => {
      adapter.setEventCallback(() => {});
    };
  }, [handleCheckoutComplete]);

  useEffect(() => {
    if (!isPaddleBillingLoaded || !selectedPriceId || !accountData?.id || checkoutOpened.current)
      return;

    PaddleBillingAdapter.getInstance().openInlineCheckout({
      customerId,
      priceId: selectedPriceId,
      containerSelector: style.paddleContainer,
      user: {
        userId: accountData.id,
        email: accountData?.email ?? "",
        countryCode: accountData?.countryCode ?? "",
      },
    });
    checkoutOpened.current = true;
  }, [
    customerId,
    isPaddleBillingLoaded,
    selectedPriceId,
    accountData?.id,
    accountData?.email,
    accountData?.countryCode,
  ]);

  const handlePeriodChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const period = e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly;
      setSelectedPeriod(period);

      const newPriceId = prices.find((p) => p.period === period)?.priceId;
      if (newPriceId) {
        PaddleBillingAdapter.getInstance().updateCheckoutItems([
          { priceId: newPriceId, quantity: 1 },
        ]);
      }
    },
    [prices]
  );

  if (paddleBillingError) {
    return (
      <div className={style.wrapper}>
        <div className={classNames(style.pageWrapper, style.show)}>
          <div className={style.headerWrapper}>
            <Heading type="h2">Something went wrong</Heading>
            <div className={style.subtitle}>
              Failed to load payment provider. Please try again later.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLoading = !isPaddleBillingLoaded || !isCheckoutReady;

  return (
    <div className={style.wrapper}>
      {(isLoading || isVerifyPaddleBillingTransactionPending) && (
        <div className={style.spinnerOverlay}>
          <SpinnerCenter />
        </div>
      )}
      <div
        className={classNames(
          style.pageWrapper,
          !isLoading && !isVerifyPaddleBillingTransactionPending && style.show
        )}
      >
        <div className={style.headerWrapper}>
          <Heading type="h2">Subscribe Now</Heading>
          <div className={style.subtitle}>Access to all features without limits.</div>
        </div>
        <div className={style.inlineCheckoutWrapper}>
          <div className={style.summary}>
            <div className={style.summaryTitle}>Summary</div>
            {checkoutSummary && (
              <>
                {checkoutSummary.items.map((item) => (
                  <div key={item.name} className={style.summaryRow}>
                    <span>{item.name}</span>
                    <span>{formatCurrency(item.subtotal, checkoutSummary.currencyCode)}</span>
                  </div>
                ))}
                {checkoutSummary.discount > 0 && (
                  <div className={style.summaryRow}>
                    <span>Limited Offer</span>
                    <span className={style.discountAmount}>
                      {formatCurrency(-checkoutSummary.discount, checkoutSummary.currencyCode)}
                    </span>
                  </div>
                )}
                {checkoutSummary.tax > 0 && (
                  <div className={style.summaryRow}>
                    <span>VAT</span>
                    <span>{formatCurrency(checkoutSummary.tax, checkoutSummary.currencyCode)}</span>
                  </div>
                )}
                <div className={style.summaryDivider} />
                <div className={style.summaryRow}>
                  <span>Total due today</span>
                  <span>{formatCurrency(checkoutSummary.total, checkoutSummary.currencyCode)}</span>
                </div>
                {selectedPeriod === SubscriptionPeriod.Yearly && (
                  <div className={style.summaryNote}>
                    Your subscription will automatically expire in one year.
                  </div>
                )}
                {selectedPeriod === SubscriptionPeriod.Monthly && (
                  <div className={style.summaryNote}>
                    Your subscription will automatically expire in one month.
                  </div>
                )}
              </>
            )}
          </div>
          <div className={style.paymentDetails}>
            <div className={style.subgroup}>
              <div className={style.checkoutLineText}>Billing Period</div>
              <SegmentedControl
                className={style.periodPicker}
                checked={selectedPeriod === SubscriptionPeriod.Monthly}
                onChange={handlePeriodChange}
                optionA="Yearly"
                optionB="Monthly"
              />
            </div>
            <div className={style.subgroup}>
              <div className={style.checkoutLineText}>Payment method</div>
              <div className={style.paddleContainer} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
